import { G } from '@mobily/ts-belt';
import { Box } from '@mui/material';
import { ReactNode } from 'react';
import {
  NumberField,
  RecordContextProvider,
  TextField,
  useGetList,
  useRecordContext,
} from 'react-admin';
import { match } from 'ts-pattern';

import { B2bCompanyDocumentDetails, B2bCompanyDocumentType } from '@Common/types/bo/b2b/companies';
import {
  BoSavingsFundsRequestDetails,
  CampaignSavingsFundsRequestDetails,
  OtherSavingsFundsRequestDetails,
  SavingsFundsRequestDetails,
} from '@Common/types/bo/b2b/savings_funds_requests';

import { ShowFields, ShowSection } from '@sh4res/bo-common/components';
import { b2bCampaignTypeChoices } from '@sh4res/bo-common/components/B2bCampaigns/choices';
import { CoPaymentStatus } from '@sh4res/bo-common/components/B2bSavingsFundRequests/CoPaymentStatus';
import {
  SavingsFundsRequestStatus,
  savingsFundsRequestTypeChoices,
} from '@sh4res/bo-common/components/B2bSavingsFundRequests/choices';
import { CampaignsPayoutsList } from '@sh4res/bo-common/components/B2bSavingsFundRequests/orders_section/CampaignsPayoutsList';
import { FullyManagedContributionsList } from '@sh4res/bo-common/components/B2bSavingsFundRequests/orders_section/FullyManagedContributionsList';
import { LinkedOrdersList } from '@sh4res/bo-common/components/B2bSavingsFundRequests/orders_section/LinkedOrdersList';
import { SavingsFundRequestEmployeesList } from '@sh4res/bo-common/components/B2bSavingsFundRequests/orders_section/SavingsFundRequestEmployeesList';
import { CurrencyField, IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { DocumentListViewerLink } from '@sh4res/bo-common/fields/DocumentListViewerLink';
import { ResourceBasePath, useCanAccess } from '@sh4res/bo-common/hooks';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const CampaignOperationSummaryDocument = ({
  resourceBasePath,
}: {
  resourceBasePath: ResourceBasePath;
}) => {
  const record = useRecordContext<CampaignSavingsFundsRequestDetails>();

  const { data } = useGetList<B2bCompanyDocumentDetails>(`${resourceBasePath}companies/documents`, {
    filter: {
      b2b_campaign_id: record?.campaign_id,
      company_id: record?.b2b_company_id,
      type: 'campaign_operation_summary',
      is_archived: false,
    },
    sort: { field: 'created_at', order: 'DESC' },
    pagination: { page: 1, perPage: 1 },
  });

  return data && data.length > 0 ? (
    <DocumentListViewerLink record={data[0]} urlSource="document_url" nameSource="document_name" />
  ) : (
    <TextField data-testid="BoExternalCampaignOperationSummaryDocument_EmptyTextField" />
  );
};

const SavingsFundRequestDocument = ({
  type,
  resourceBasePath,
}: {
  type: 'matching_contribution' | 'campaign';
  resourceBasePath: ResourceBasePath;
}) => {
  const record = useRecordContext<CampaignSavingsFundsRequestDetails>();

  const documentType: B2bCompanyDocumentType = match(type)
    .with('matching_contribution', () => 'matching_contribution_fund_request' as const)
    .with('campaign', () => 'campaign_fund_request' as const)
    .exhaustive();

  const { data } = useGetList<B2bCompanyDocumentDetails>(`${resourceBasePath}companies/documents`, {
    filter: {
      b2b_campaign_id: record?.campaign_id,
      savings_funds_request_id: record?.id,
      company_id: record?.b2b_company_id,
      type: documentType,
      is_archived: false,
    },
    sort: { field: 'created_at', order: 'DESC' },
    pagination: { page: 1, perPage: 1 },
  });

  return data && data.length > 0 ? (
    <DocumentListViewerLink record={data[0]} urlSource="document_url" nameSource="document_name" />
  ) : (
    <TextField data-testid="BoExternalCampaignFundRequestDocument_EmptyTextField" />
  );
};

const CoPaymentStatusRow = ({ action }: { action?: ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <CoPaymentStatus />
    {action}
  </Box>
);

const StatusRow = ({ action }: { action?: ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <SavingsFundsRequestStatus />
    {action}
  </Box>
);

type EmployeesTotalsRecord = Pick<
  OtherSavingsFundsRequestDetails,
  | 'id'
  | 'currency'
  | 'number_of_eligible_employees'
  | 'total_gross_amount'
  | 'total_contribution_amount'
  | 'total_net_amount'
> & {
  total_csg_tax_amount: number;
  total_crds_tax_amount: number;
};

/** The amounts requested from the company, employees and self employed together */
const EmployeesTotals = () => {
  const record = useRecordContext<OtherSavingsFundsRequestDetails>();

  if (!record) {
    return null;
  }

  const totals: EmployeesTotalsRecord = {
    id: record.id,
    currency: record.currency,
    number_of_eligible_employees: record.number_of_eligible_employees,
    total_gross_amount: record.total_gross_amount,
    total_contribution_amount: record.total_contribution_amount,
    total_csg_tax_amount:
      (record.ts_user_total_csg_tax_amount ?? 0) + (record.tns_user_total_csg_tax_amount ?? 0),
    total_crds_tax_amount:
      (record.ts_user_total_crds_tax_amount ?? 0) + (record.tns_user_total_crds_tax_amount ?? 0),
    total_net_amount: record.total_net_amount,
  };

  return (
    <RecordContextProvider value={totals}>
      <ShowFields<EmployeesTotalsRecord>
        labelPrefix="resources.bo_common.b2b.savings_funds_requests.employees.totals"
        fields={{
          number_of_eligible_employees: (
            <NumberField data-testid="BoExternalSavingsFundRequestDetails_TotalNbEligibleField" />
          ),
          total_gross_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_EmployeesTotalGrossAmountField"
            />
          ),
          total_contribution_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TotalContributionAmountField"
            />
          ),
          total_csg_tax_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TotalCsgTaxAmountField"
            />
          ),
          total_crds_tax_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TotalCrdsTaxAmountField"
            />
          ),
          total_net_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_EmployeesTotalNetAmountField"
            />
          ),
        }}
      />
    </RecordContextProvider>
  );
};

const SocialStatusBreakdowns = () => (
  <>
    <ShowSection label="resources.bo_common.b2b.savings_funds_requests.employees_breakdown.title">
      <ShowFields<OtherSavingsFundsRequestDetails>
        resource="bo_common.b2b.savings_funds_requests"
        fields={{
          ts_user_total_nb_eligible: (
            <NumberField data-testid="BoExternalSavingsFundRequestDetails_TsUserTotalNbEligibleField" />
          ),
          ts_user_total_gross_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TsUserTotalGrossAmountField"
            />
          ),
          ts_user_total_contribution_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TsUserTotalContributionAmountField"
            />
          ),
          ts_user_total_csg_tax_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TsUserTotalCsgTaxAmountField"
            />
          ),
          ts_user_total_crds_tax_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TsUserTotalCrdsTaxAmountField"
            />
          ),
          ts_user_total_net_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TsUserTotalNetAmountField"
            />
          ),
        }}
      />
    </ShowSection>
    <ShowSection label="resources.bo_common.b2b.savings_funds_requests.contractors_breakdown.title">
      <ShowFields<OtherSavingsFundsRequestDetails>
        resource="bo_common.b2b.savings_funds_requests"
        fields={{
          tns_user_total_nb_eligible: (
            <NumberField data-testid="BoExternalSavingsFundRequestDetails_TnsUserTotalNbEligibleField" />
          ),
          tns_user_total_gross_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TnsUserTotalGrossAmountField"
            />
          ),
          tns_user_total_contribution_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TnsUserTotalContributionAmountField"
            />
          ),
          tns_user_total_csg_tax_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TnsUserTotalCsgTaxAmountField"
            />
          ),
          tns_user_total_crds_tax_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TnsUserTotalCrdsTaxAmountField"
            />
          ),
          tns_user_total_net_amount: (
            <CurrencyField
              currency="currency"
              testId="BoExternalSavingsFundRequestDetails_TnsUserTotalNetAmountField"
            />
          ),
        }}
      />
    </ShowSection>
  </>
);

export const SavingsFundRequestDetails = ({
  type,
  resourceBasePath,
  statusAction,
  coPaymentStatusAction,
}: {
  type: SavingsFundsRequestDetails['type'] | null;
  resourceBasePath: ResourceBasePath;
  statusAction?: ReactNode;
  coPaymentStatusAction?: ReactNode;
}) => {
  const record = useRecordContext<BoSavingsFundsRequestDetails>();
  const canAccessOrders = useCanAccess({
    resource: `${resourceBasePath}orders/fully_managed_plan_contributions`,
    action: 'list',
  });
  const canAccessWithdrawals = useCanAccess({
    resource: `${resourceBasePath}withdrawals`,
    action: 'show',
  });
  const canAccessEmployees = useCanAccess({
    resource: `${resourceBasePath}savings_funds_requests/employees`,
    action: 'list',
  });

  if (!record) {
    return null;
  }

  const isCcbWithdrawal = type === 'ccb_withdrawal';
  // Recurring matching contributions: MC campaigns have the 'campaign' type
  const isMatchingContribution = type === 'matching_contribution';
  // External users get the per employee breakdown only, internal ones the orders behind it too
  const showOrders = !isMatchingContribution || !isBoExternal();

  return (
    <>
      <ShowSection label="general.sections.details">
        <ShowFields<
          BoSavingsFundsRequestDetails & { campaign_operation_summary: string; due_date: string }
        >
          resource="bo_common.b2b.savings_funds_requests"
          fields={{
            ...(!isBoExternal() && {
              id: (
                <IdField
                  reference={`${resourceBasePath}savings_funds_requests`}
                  data-testid="BoExternalSavingsFundRequestDetails_IdField"
                />
              ),
              b2b_company_id: (
                <IdField
                  reference={`${resourceBasePath}companies`}
                  data-testid="BoExternalSavingsFundRequestDetails_CompanyIdField"
                />
              ),
            }),
            date: (
              <UTCDateField
                options={{ year: 'numeric', month: '2-digit' }}
                data-testid="BoExternalSavingsFundRequestDetails_DateField"
              />
            ),
            status: <StatusRow action={statusAction} />,
            ...(!isBoExternal() &&
              G.isNotNullable(record.execution_error) && {
                execution_error: (
                  <TextField data-testid="BoExternalSavingsFundRequestDetails_ExecutionErrorField" />
                ),
              }),
            type: (
              <SelectChipField
                source="type"
                choices={savingsFundsRequestTypeChoices}
                data-testid="BoExternalSavingsFundRequestDetails_TypeField"
              />
            ),
            ...(record.type === 'ccb_withdrawal' &&
              canAccessWithdrawals &&
              G.isNotNullable(record.b2b_withdrawal_id) && {
                b2b_withdrawal_id: (
                  <IdField
                    reference={`${resourceBasePath}withdrawals`}
                    data-testid="BoExternalSavingsFundRequestDetails_WithdrawalIdField"
                  />
                ),
              }),
            ...(record.type === 'ccb_withdrawal' && {
              due_date: (
                <UTCDateField
                  source="date"
                  showTime={false}
                  data-testid="BoExternalSavingsFundRequestDetails_DueDateField"
                />
              ),
            }),
            ...(G.isNotNullable(record.payment_reference) && {
              payment_reference: (
                <TextField data-testid="BoExternalSavingsFundRequestDetails_PaymentReferenceField" />
              ),
            }),
            co_payment_status: <CoPaymentStatusRow action={coPaymentStatusAction} />,
            total_gross_amount: (
              <CurrencyField
                currency="currency"
                testId="BoExternalSavingsFundRequestDetails_TotalGrossAmountField"
              />
            ),
            total_net_amount: (
              <CurrencyField
                currency="currency"
                testId="BoExternalSavingsFundRequestDetails_TotalNetAmountField"
              />
            ),
            ...(record.type === 'campaign' && {
              campaign_id: (
                <IdField
                  reference={`${resourceBasePath}campaigns`}
                  data-testid="BoExternalSavingsFundRequestDetails_CampaignIdField"
                />
              ),
              campaign_type: (
                <SelectChipField
                  choices={b2bCampaignTypeChoices}
                  data-testid="BoExternalSavingsFundRequestDetails_CampaignTypeField"
                />
              ),
              campaign_operation_summary: (
                <CampaignOperationSummaryDocument resourceBasePath={resourceBasePath} />
              ),
            }),
            ...((record.type === 'campaign' || record.type === 'matching_contribution') && {
              fund_request_document: (
                <SavingsFundRequestDocument
                  type={record.type}
                  resourceBasePath={resourceBasePath}
                />
              ),
            }),
            created_at: (
              <UTCDateField
                showTime={!isBoExternal()}
                data-testid="BoExternalSavingsFundRequestDetails_CreatedAtField"
              />
            ),
            updated_at: (
              <UTCDateField
                showTime={!isBoExternal()}
                data-testid="BoExternalSavingsFundRequestDetails_UpdatedAtField"
              />
            ),
          }}
        />
      </ShowSection>
      {isMatchingContribution ? (
        canAccessEmployees && (
          <ShowSection label="resources.bo_common.b2b.savings_funds_requests.employees.section_title">
            <EmployeesTotals />
            <SavingsFundRequestEmployeesList resourceBasePath={resourceBasePath} />
          </ShowSection>
        )
      ) : (
        <SocialStatusBreakdowns />
      )}
      {match(type)
        .with('matching_contribution', 'campaign', 'ccb_withdrawal', () =>
          canAccessOrders && showOrders ? (
            <ShowSection
              label="resources.bo_common.b2b.savings_funds_requests.orders.section_title"
              subtitle={
                isCcbWithdrawal
                  ? undefined
                  : 'resources.bo_common.b2b.savings_funds_requests.orders.subtitle'
              }>
              <LinkedOrdersList resourceBasePath={resourceBasePath} />
              {!isCcbWithdrawal && (
                <ShowSection
                  label="resources.bo_common.b2b.savings_funds_requests.fully_managed_plan_contributions.section_title"
                  noCard>
                  <FullyManagedContributionsList resourceBasePath={resourceBasePath} />
                </ShowSection>
              )}
            </ShowSection>
          ) : null,
        )
        .otherwise(() => null)}
      {match(type)
        .with('campaign', () => (
          <ShowSection label="resources.bo_common.b2b.savings_funds_requests.payouts.section_title">
            <CampaignsPayoutsList resourceBasePath={resourceBasePath} />
          </ShowSection>
        ))
        .otherwise(() => null)}
    </>
  );
};
